import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import FilterDropdown from './FilterDropdown';
import { useFieldValues } from '../../hooks/useFieldValues';
import { isUnaryOperator, isSingleValueOperator, isListOperator } from '../../utils/query/operatorArity';
import { formatFilterValueForDisplay } from '../../utils/query/formatFilterValue';
import styles from '../../styles/query/FilterPill.module.scss';

/**
 * Filter Pill Component (JIRA-style)
 * 
 * Displays a filter pill with dropdown for selecting values
 * Supports active/inactive states and dropdown interaction
 * 
 * @param {Object} filter - Filter pill object with { id, field, operator, values }
 * @param {Object} schema - Query schema for field metadata
 * @param {function} onEdit - Edit handler (id, updates)
 * @param {function} onRemove - Remove handler (id)
 * @param {function} onApply - Apply handler (triggers query execution)
 * @param {boolean} isFixed - Whether this filter is fixed (cannot be removed, e.g., Project)
 */
export function FilterPill({ 
  filter, 
  schema, 
  onEdit, 
  onRemove,
  onApply,
  isFixed = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const pillRef = useRef(null);
  const fieldMeta = schema?.fields?.find(f => f.name === filter.field);
  // Ref to track latest operator to avoid stale props in handleSelectionChange
  const latestOperatorRef = useRef(filter.operator || '=');
  
  // Use hook to fetch field values
  const { values: options, loading: optionsLoading } = useFieldValues(fieldMeta);
  
  // Update ref when filter.operator changes
  useEffect(() => {
    latestOperatorRef.current = filter.operator || '=';
  }, [filter.operator]);
  
  const handlePillClick = (e) => {
    // Don't open if clicking remove button
    if (e.target.closest('[data-remove-button]')) {
      return;
    }
    setIsOpen(!isOpen);
  };
  
  const handleOperatorChange = (newOperator) => {
    if (onEdit) {
      // Update ref immediately to avoid stale values in handleSelectionChange
      latestOperatorRef.current = newOperator;
      
      let newValues = filter.values || [];
      
      // Normalize values based on new operator's arity
      if (isUnaryOperator(newOperator)) {
        newValues = [];
      } else if (isSingleValueOperator(newOperator) && newValues.length > 1) {
        newValues = [newValues[0]];
      }
      
      onEdit(filter.id, { operator: newOperator, values: newValues });
      
      // Trigger query execution immediately when operator changes
      setTimeout(() => {
        if (onApply) {
          onApply();
        }
      }, 0);
    }
  };
  
  const handleSelectionChange = (newValues) => {
    if (onEdit) {
      // Use ref to get latest operator (avoids stale props when onOperatorChange and onSelectionChange are called in quick succession)
      let operator = latestOperatorRef.current;
      const fieldMeta = schema?.fields?.find(f => f.name === filter.field);
      const isDateField = fieldMeta?.kind?.toLowerCase() === 'date';
      const availableOperators = fieldMeta?.operators || [];
      
      const valueCount = newValues ? newValues.length : 0;
      
      // For date fields, preserve the operator that was explicitly set via onOperatorChange
      // Don't auto-adjust operators for date fields - they're controlled by the Relative/Absolute UI
      if (!isDateField) {

        // Only auto-adjust from single-value to list when multiple values are selected
        if (isSingleValueOperator(operator) && valueCount > 1) {
          if (availableOperators.includes('in')) {
            operator = 'in';
            latestOperatorRef.current = operator; // Update ref
          }
        }
        // Removed: Don't auto-adjust from list operator to single-value operator
        // This was causing the bug where selecting first module with "in" operator would reset to "="
      }
      
      onEdit(filter.id, { values: newValues, operator });
      // Trigger query execution immediately (instant apply)
      // Always trigger apply, even for empty values (to remove filter from query)
      // Use setTimeout to ensure Redux state update is processed first
      setTimeout(() => {
        if (onApply) {
          onApply();
        }
      }, 0);
    }
  };
  
  const handleClear = () => {
    // Clear selection but don't trigger query execution
    handleSelectionChange([]);
    // Don't call onApply here - user needs to click Apply button
  };
  
  const handleRemove = (e) => {
    e.stopPropagation();
    if (onRemove && !isFixed) {
      onRemove(filter.id);
    }
  };
  
  // Determine if filter is active (has selected values or is unary operator)
  // Unary operators (isEmpty, isNotEmpty) are active even without values
  const isActive = isUnaryOperator(filter.operator) || (filter.values && filter.values.length > 0);
  
  // Get display text
  const getDisplayText = () => {
    const fieldLabel = fieldMeta?.label || filter.field;
    const operator = filter.operator || '=';
    const selectedCount = filter.values?.length || 0;
    
    // Special handling for between operator with two dates
    if (operator === 'between' && selectedCount >= 2) {
      const [startRaw, endRaw] = filter.values;
      // Always use the shared display formatter so we handle {date, timezone}
      // and other complex value types consistently.
      const startLabel = formatFilterValueForDisplay(startRaw, fieldMeta);
      const endLabel = formatFilterValueForDisplay(endRaw, fieldMeta);

      return `${fieldLabel} between ${startLabel} – ${endLabel}`;
    }
    
    if (selectedCount === 0) {
      return `${fieldLabel} ${operator}`;
    }
    
    if (selectedCount === 1) {
      const value = filter.values[0];
      let valueLabel;
      
      // Format value for display (UI layer responsibility)
      valueLabel = formatFilterValueForDisplay(value, fieldMeta);
      
      // Truncate long values
      const displayValue = valueLabel.length > 20 ? valueLabel.substring(0, 20) + '...' : valueLabel;
      return `${fieldLabel} ${operator} ${displayValue}`;
    }
    
    // For multiple dates (list operators) - improved display
    if (selectedCount > 1) {
      // Show first date formatted, then count
      const firstValue = filter.values[0];
      const firstDateLabel = formatFilterValueForDisplay(firstValue, fieldMeta);
      
      return `${fieldLabel} ${operator} ${firstDateLabel} +${selectedCount - 1} more`;
    }
    
    return `${fieldLabel} ${operator} (${selectedCount} selected)`;
  };
  
  const displayText = getDisplayText();
  // Get operator from filter, default to first operator from field metadata
  // If field has operators, use first one; otherwise default to '='
  const operator = filter.operator || (fieldMeta?.operators?.[0] || '=');
  
  return (
    <>
      <button
        ref={pillRef}
        type="button"
        className={`${styles.filterPill} ${isActive ? styles.active : ''} ${isFixed ? styles.fixed : ''}`}
        onClick={handlePillClick}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className={styles.pillText}>{displayText}</span>
        {isOpen ? (
          <ChevronUpIcon className={styles.pillIcon} />
        ) : (
          <ChevronDownIcon className={styles.pillIcon} />
        )}
        {!isFixed && onRemove && (
          <button
            type="button"
            data-remove-button
            className={styles.removeButton}
            onClick={handleRemove}
            aria-label="Remove filter"
          >
            ×
          </button>
        )}
      </button>
      
      {isOpen && (
        <FilterDropdown
          field={fieldMeta || { name: filter.field }}
          operator={operator}
          selectedValues={filter.values || []}
          options={options}
          loading={optionsLoading}
          schema={schema}
                    onOperatorChange={handleOperatorChange}
                    onSelectionChange={handleSelectionChange}
                    onApply={onApply}
                    onClear={handleClear}
          onClose={() => setIsOpen(false)}
          anchorEl={pillRef.current}
        />
      )}
    </>
  );
}

export default FilterPill;
